import { ArrowDropDownOutlined } from '@mui/icons-material'
import {
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material'
import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  pending: boolean
  onAdd: () => Promise<void>
  onReplace: () => Promise<void>
}

export const ActivityCopyButton = ({ pending, onAdd, onReplace }: Props) => {
  const { t } = useTranslation()
  const anchorRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const close = () => {
    setOpen(false)
    anchorRef.current
      ?.querySelector<HTMLButtonElement>('[aria-haspopup]')
      ?.focus()
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <ButtonGroup
          ref={anchorRef}
          variant="outlined"
          size="small"
          color="secondary"
          disabled={pending}
        >
          <Button
            onClick={() => {
              setOpen(false)
              void onAdd()
            }}
            startIcon={pending ? <CircularProgress size={20} /> : undefined}
          >
            {t(
              'project:apply-ws-to-other-parts',
              'add working step to other parts',
            )}
          </Button>
          <Button
            aria-controls={open ? menuId : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            aria-label={t(
              'project:copy-working-step-options',
              'select working step copy options',
            )}
            onClick={() => setOpen((value) => !value)}
          >
            <ArrowDropDownOutlined />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{ zIndex: 420000 }}
          open={open}
          anchorEl={anchorRef.current}
          transition
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'left top' : 'left bottom',
              }}
            >
              <Paper>
                <MenuList
                  id={menuId}
                  autoFocusItem={open}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape' || event.key === 'Tab') close()
                  }}
                >
                  <MenuItem
                    disabled={pending}
                    onClick={() => {
                      close()
                      void onReplace()
                    }}
                  >
                    <div>
                      <Typography variant="body2">
                        {t(
                          'project:replace-working-step-data-in-other-parts',
                          'replace working step data in other parts',
                        )}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t(
                          'project:replace-working-step-data-help',
                          'Replaces data and comments on all matching working steps. Missing steps are skipped.',
                        )}
                      </Typography>
                    </div>
                  </MenuItem>
                </MenuList>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  )
}
